import { AxiosError } from 'axios';
import { AxiosService } from '../../../axios/axios.service';
import type { tMatchingQuery } from '../Directory.matching';
import { tAxiosCustomConfig } from '../../../axios/axios.types';
import { rejectGatewayErrorBody } from '../../../axios/gatewayErrorBody';
import {
  DIRECTORY_ERROR_REASONS,
  tBulkOperation,
  tCapturedSelection,
  tDirectoryApiError,
  tDirectoryErrorReason,
  tOperationAction,
  tOperationPage,
  tOperationPreview,
  tSelectionRef,
} from './DirectoryOperations.types';

type tEnvelope<T> = { success: boolean; code: number; data: T };

/** GETs share their path with the pre-v1 status routes; this picks v1. */
const CONTRACT_PARAMS = { contract_version: 'directory.v1' };

/**
 * The contract's refusals (409, 410, 422, 429...) are answers the UI explains,
 * not incidents, so they do not go to Sentry.
 */
function config(options: { idempotencyKey?: string; params?: object }): tAxiosCustomConfig {
  return {
    skipSentryCapture: true,
    ...(options.params && { params: options.params }),
    ...(options.idempotencyKey && {
      headers: { 'Idempotency-Key': options.idempotencyKey },
    }),
  };
}

function base(workspaceId: string): string {
  return `/workspace/${workspaceId}/leads`;
}

/**
 * The envelope's data. Behind production's gateway a 503 or 504 arrives as a
 * 200 with the gateway's own body (gatewayErrorBody.ts): it is thrown as the
 * error it is, never returned as an empty answer. The service's reason does
 * not survive the gateway and is never guessed (see parseDirectoryError).
 */
function dataOf<T>(envelope: tEnvelope<T>): T {
  return rejectGatewayErrorBody(envelope).data;
}

/**
 * directory.v1 on bff-web. Every write that creates something carries an
 * Idempotency-Key: replaying the same key returns the same resource, which is
 * how a lost response is recovered without doing the work twice.
 */
export const DirectoryOperationsService = {
  async sharedTags(
    workspaceId: string,
    selection: tSelectionRef,
  ): Promise<{
    selection_id: string;
    revision: number;
    selected_count: number;
    shared_tag_ids: number[];
  }> {
    return dataOf(
      await AxiosService.post<
        { expected_revision: number },
        tEnvelope<{
          selection_id: string;
          revision: number;
          selected_count: number;
          shared_tag_ids: number[];
        }>
      >(
        `${base(workspaceId)}/selections/${selection.id}/shared-tags`,
        { expected_revision: selection.revision },
        config({}),
      ),
    );
  },

  async readSelection(
    workspaceId: string,
    selectionId: string,
  ): Promise<tCapturedSelection> {
    return dataOf(
      await AxiosService.get<tEnvelope<tCapturedSelection>>(
        `${base(workspaceId)}/selections/${selectionId}`,
        config({}),
      ),
    );
  },

  async patchSelection(
    workspaceId: string,
    request: {
      selectionId: string;
      expectedRevision: number;
      exclude: string[];
      reinclude: string[];
    },
  ): Promise<tCapturedSelection> {
    return dataOf(
      await AxiosService.patch<
        { expected_revision: number; exclude: string[]; reinclude: string[] },
        tEnvelope<tCapturedSelection>
      >(
        `${base(workspaceId)}/selections/${request.selectionId}`,
        {
          expected_revision: request.expectedRevision,
          exclude: request.exclude,
          reinclude: request.reinclude,
        },
        config({}),
      ),
    );
  },

  async membership(
    workspaceId: string,
    request: {
      selectionId: string;
      leadIds: string[];
    },
  ): Promise<{ members: Record<string, boolean> }> {
    return dataOf(
      await AxiosService.post<
        { lead_ids: string[] },
        tEnvelope<{ members: Record<string, boolean> }>
      >(
        `${base(workspaceId)}/selections/${request.selectionId}/membership`,
        {
          lead_ids: request.leadIds,
        },
        config({}),
      ),
    );
  },

  async captureExplicit(
    workspaceId: string,
    request: { leadIds: string[]; idempotencyKey: string },
  ): Promise<tCapturedSelection> {
    const response = await AxiosService.post<
      { mode: 'explicit'; lead_ids: string[] },
      tEnvelope<tCapturedSelection>
    >(
      `${base(workspaceId)}/selections`,
      { mode: 'explicit', lead_ids: request.leadIds },
      config({ idempotencyKey: request.idempotencyKey }),
    );
    return dataOf(response);
  },

  /** Every lead that matches the view's predicate, captured on the server. */
  async captureMatching(
    workspaceId: string,
    request: { query: tMatchingQuery; idempotencyKey: string },
  ): Promise<tCapturedSelection> {
    const response = await AxiosService.post<
      { mode: 'matching'; query: tMatchingQuery },
      tEnvelope<tCapturedSelection>
    >(
      `${base(workspaceId)}/selections`,
      { mode: 'matching', query: request.query },
      config({ idempotencyKey: request.idempotencyKey }),
    );
    return dataOf(response);
  },

  async preview(
    workspaceId: string,
    request: { selection: tSelectionRef; action: tOperationAction },
  ): Promise<tOperationPreview> {
    const response = await AxiosService.post<
      { selection: tSelectionRef; action: tOperationAction },
      tEnvelope<tOperationPreview>
    >(`${base(workspaceId)}/bulk-operations/preview`, request, config({}));
    return dataOf(response);
  },

  async create(
    workspaceId: string,
    request: { previewId: string; idempotencyKey: string },
  ): Promise<tBulkOperation> {
    const response = await AxiosService.post<
      { preview_id: string },
      tEnvelope<tBulkOperation>
    >(
      `${base(workspaceId)}/bulk-operations`,
      { preview_id: request.previewId },
      config({ idempotencyKey: request.idempotencyKey }),
    );
    return dataOf(response);
  },

  async get(workspaceId: string, operationId: string): Promise<tBulkOperation> {
    const response = await AxiosService.get<tEnvelope<tBulkOperation>>(
      `${base(workspaceId)}/bulk-operations/${operationId}`,
      config({ params: CONTRACT_PARAMS }),
    );
    return dataOf(response);
  },

  async list(workspaceId: string): Promise<tOperationPage> {
    const response = await AxiosService.get<tEnvelope<tOperationPage>>(
      `${base(workspaceId)}/bulk-operations`,
      config({ params: CONTRACT_PARAMS }),
    );
    return dataOf(response);
  },

  async undo(
    workspaceId: string,
    request: { operationId: string; idempotencyKey: string },
  ): Promise<tBulkOperation> {
    const response = await AxiosService.post<Record<string, never>, tEnvelope<tBulkOperation>>(
      `${base(workspaceId)}/bulk-operations/${request.operationId}/undo`,
      {},
      config({ idempotencyKey: request.idempotencyKey }),
    );
    return dataOf(response);
  },
};

const KNOWN_REASONS: ReadonlySet<string> = new Set(DIRECTORY_ERROR_REASONS);

/**
 * Reads a directory.v1 refusal: `{success:false, code, error:{reason,
 * retryable, details}}`. No answer at all is `network` (retryable, and never
 * proof that nothing happened); an answer outside the closed set is
 * `unknown`, never a guess. That includes a 503 production's gateway answered
 * without the service's body: it may be any of the contract's three 503
 * refusals, and only `policy_unavailable` may hand the picks to the previous
 * dialog.
 */
export function parseDirectoryError(error: unknown): tDirectoryApiError {
  const response = (error as AxiosError | undefined)?.response;
  if (!response) return { status: 0, reason: 'network', retryable: true };

  const body = response.data as
    | { error?: { reason?: string; retryable?: boolean; details?: tDirectoryApiError['details'] } }
    | undefined;
  const reason = body?.error?.reason;
  const retryAfter = Number(
    (response.headers as Record<string, string> | undefined)?.['retry-after'],
  );

  return {
    status: response.status,
    reason:
      reason && KNOWN_REASONS.has(reason) ? (reason as tDirectoryErrorReason) : 'unknown',
    retryable: body?.error?.retryable ?? response.status >= 500,
    ...(Number.isFinite(retryAfter) && retryAfter > 0 && {
      retryAfterSeconds: retryAfter,
    }),
    ...(body?.error?.details && { details: body.error.details }),
  };
}
